import { Box, styled, SxProps, Theme } from "@mui/material";
import { gray, error as errorColor, warning } from "configs/colors";
import { rem, getPrimaryPaletteColor, generateFormFieldStyles } from "utils";

import { BRACKET_HIGHLIGHT_NAME } from "./MentionsInput.hooks";
import { MentionsInputType } from "./MentionsInput.types";

export const Container = styled(Box)<{ disabled?: string; error?: string }>(
  ({ theme, disabled, error }) => {
    const styles = generateFormFieldStyles({ theme });
    const isDisabled = disabled === "true";
    const isError = error === "true";
    return {
      display: "block",
      width: "100%",

      // Input Label
      "& > .MuiFormLabel-root": {
        marginBottom: rem(6),
      },

      // Helper text
      ".MuiFormHelperText-root": {
        marginTop: rem(6),
        "&, &.Mui-disabled": {
          color: gray[600],
        },
        "&.Mui-error": {
          color: theme.palette.error.main,
        },
      },

      // Dấu ngoặc khớp (type="formula")
      [`& ::highlight(${BRACKET_HIGHLIGHT_NAME})`]: {
        backgroundColor: warning[200],
      },

      // Input
      textarea: {
        "&::-webkit-input-placeholder": {
          color: "currentColor",
          opacity: 0.42,
          transition: theme.transitions.create(["opacity"]),
        },
        ...(isDisabled
          ? {
              "-webkit-text-fill-color": styles.disabled.color,
            }
          : {
              borderColor: isError
                ? `${errorColor[600]} !important`
                : undefined,
              "&:hover": {
                borderColor: `${
                  styles[isError ? "error" : "normal"].hover.borderColor
                } !important`,
              },
              "&:focus": {
                borderColor: `${
                  styles[isError ? "error" : "normal"].focus.borderColor
                } !important`,
                boxShadow: `${
                  styles[isError ? "error" : "normal"].focus.boxShadow
                } !important`,
              },
            }),
      },
    };
  }
);

export const getMentionsInputStyle = (params: {
  theme: Theme;
  disabled?: boolean;
  type?: MentionsInputType;
  suggestionsSx?: SxProps<Theme>;
}) => {
  const { theme, disabled, type, suggestionsSx } = params;
  const styles = generateFormFieldStyles({ theme });
  return {
    control: {
      backgroundColor: disabled
        ? styles.disabled.backgroundColor
        : styles.normal.backgroundColor,
      borderRadius: theme.shape.borderRadius,
      color: disabled ? theme.palette.text.disabled : gray[900],
    },
    input: {
      color: "currentColor",
      outline: "none",
      padding: `${rem(6.5)} ${rem(12)}`,
      fontSize: rem(14),
      lineHeight: rem(20),
      border: `1px solid ${
        disabled ? styles.disabled.borderColor : styles.normal.borderColor
      }`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: styles.normal.boxShadow,
      transition: theme.transitions.create([
        "border-color",
        "background-color",
        "box-shadow",
      ]),
    },
    highlighter: {
      padding: `${rem(6.5)} ${rem(12)}`,
      fontSize: rem(14),
      lineHeight: rem(20),
      // `::highlight()` không vẽ lên phần tử `visibility: hidden` (mặc định của react-mentions)
      ...(type === "formula" ? { substring: { visibility: "visible" } } : {}),
    },
    suggestions: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: styles.normal.boxShadow,
      list: {
        fontSize: rem(14),
        padding: `${rem(4)} ${rem(6)}`,
        minWidth: rem(250),
        maxHeight: rem(300),
        overflowY: "auto",
      },
      item: {
        padding: `${rem(8)} ${rem(6)}`,
        borderRadius: "6px",
        transition: theme.transitions.create(["background-color"]),
        "&focused": {
          backgroundColor: gray[50],
        },
      },
      ...(suggestionsSx ?? {}),
    },
  };
};

export const getMentionStyle = (params: { theme: Theme }) => {
  const { theme } = params;
  return {
    backgroundColor: getPrimaryPaletteColor(theme)[100],
  };
};
